/**
 * Torre de control (/admin/torre-control) — panorama operativo unificado para
 * admin/logística, en una sola pantalla con dos secciones (tabs):
 *
 *   · "En vivo"   → pulso operativo en tiempo real (KPIs, mapa con capas,
 *     repartidores, alertas). Vive en <TorreVivo/>, que monta SUS hooks de
 *     datos (useTorreControlData + useActividadData, socket `/location`).
 *   · "Analítica" → el antiguo Dashboard de Performance (ModernDashboardAdmin):
 *     operativo, volumen por cliente, comparativa semanal, performance de
 *     repartidores y pendientes. Antes vivía en /admin/dashboard-old.
 *
 * Este componente es solo el shell: header, tabs y el gate de empresa
 * logística. Los hooks de datos vivos están DENTRO de TorreVivo a propósito:
 * como TabsContent desmonta el tab inactivo, con "Analítica" activa no corre
 * ningún polling ni socket del tab vivo (antes seguían corriendo los 5
 * endpoints de 90s + paquetes por repartidor + GPS aunque nadie los mirara).
 *
 * Datos REALES (sin endpoints nuevos). Gaps de back avisados en la propia UI:
 *   · SLA en riesgo por paquete (no hay deadline en el back).
 *   · Snapshot "control-tower" en una sola llamada (hoy son varias requests).
 *   · operational-summary es company-wide (no filtra por cliente).
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, RadioTower } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { EmptyState } from '../../../components/ui/EmptyState';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/Tabs';
import useUrlState from '../../../hooks/useUrlState';
import useAuth from '../../../hooks/useAuth';
import { localDateStr } from '../../../utils/formatters';
import { getOperationTimezone } from '../../../utils/timezone';
import ModernDashboardAdmin from '../adminDashboard/ModernDashboardAdmin';
import { getLogisticCompanyId } from './useTorreControlData';
import TorreVivo from './TorreVivo';

const DEFAULT_LAYERS = {
  conductores: true,
  stops: true,
  pendientes: true,
  entregados: false,
  fallidos: true,
};

export default function TorreControl() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const timezone = useMemo(() => getOperationTimezone(user), [user]);
  const today = useMemo(() => localDateStr(new Date(), timezone), [timezone]);

  // Día persistido en la URL (replace, no ensucia historial). Default = hoy ⇒
  // sin ?date la vista arranca idéntica a antes.
  const [date, setDate] = useUrlState('date', today);
  // Tab persistido en la URL con push (el back vuelve al tab anterior).
  const [tab, setTab] = useUrlState('tab', 'vivo', { push: true });
  // Las capas viven acá (no en TorreVivo) para sobrevivir al cambio de tab.
  const [layers, setLayers] = useState(DEFAULT_LAYERS);
  const toggleLayer = (id) => setLayers((s) => ({ ...s, [id]: !s[id] }));

  // Mismo gate que usa useTorreControlData internamente (misma función): sin
  // empresa logística no hay operación que monitorear (los commerce-fleet
  // quedan fuera, como siempre estuvo).
  const logisticCompanyId = getLogisticCompanyId(user);

  if (!logisticCompanyId) {
    return (
      <div className="w-full min-w-0 p-4 md:p-6">
        <Card>
          <EmptyState
            icon={<RadioTower />}
            title={t('Sin empresa logística asociada')}
            description={t('Tu usuario no tiene una empresa logística asociada, así que no hay operación que monitorear.')}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 p-4 md:p-6 space-y-4">
      {/* Título de la página */}
      <div className="min-w-0">
        <h1 className="text-xl md:text-2xl font-semibold text-fg">{t('Torre de control')}</h1>
        <p className="text-xs md:text-sm text-fg-muted mt-0.5">
          {t('Pulso de la operación en vivo y analítica de desempeño, todo en un solo lugar.')}
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList ariaLabel={t('Secciones de la torre de control')}>
          <TabsTrigger value="vivo" icon={<RadioTower />}>{t('En vivo')}</TabsTrigger>
          <TabsTrigger value="analitica" icon={<BarChart3 />}>{t('Analítica')}</TabsTrigger>
        </TabsList>

        {/* ── Sección: En vivo ─────────────────────────────────────────────── */}
        <TabsContent value="vivo" className="space-y-4">
          <TorreVivo
            date={date}
            today={today}
            onDateChange={setDate}
            layers={layers}
            onToggleLayer={toggleLayer}
          />
        </TabsContent>

        {/* ── Sección: Analítica (antes /admin/dashboard-old) ──────────────── */}
        {/* Márgenes negativos: ModernDashboardAdmin trae su propio padding,
            evitamos doblarlo con el del contenedor de la torre. */}
        <TabsContent value="analitica" className="-mx-4 md:-mx-6 -mb-4 md:-mb-6">
          <ModernDashboardAdmin />
        </TabsContent>
      </Tabs>
    </div>
  );
}
